<template>
    <div class="login-container">
      <div class="glass-card">
        <h1 class="title">Reset Your Password</h1>
        <p class="subtitle">Enter your new password below</p>
  
        <div v-if="error" class="error-message">{{ error }}</div>
         <div v-if="message" class="success-message">{{ message }}</div>
  
        <form @submit.prevent="handleResetPassword" v-if="!message && !tokenError">
           <div class="input-wrapper password-container">
             <input :type="showPassword ? 'text' : 'password'" placeholder="New Password (min 6 chars)" class="input-field" v-model="password" required />
             <span class="toggle-password" @click="showPassword = !showPassword">{{ showPassword ? '👁️' : '👁️‍🗨️' }}</span>
           </div>
           <div class="input-wrapper password-container">
             <input :type="showConfirmPassword ? 'text' : 'password'" placeholder="Confirm New Password" class="input-field" v-model="passwordConfirm" required />
              <span class="toggle-password" @click="showConfirmPassword = !showConfirmPassword">{{ showConfirmPassword ? '👁️' : '👁️‍🗨️' }}</span>
           </div>
  
          <button type="submit" class="login-btn" :disabled="loading">
            {{ loading ? 'Updating...' : 'Update Password' }}
          </button>
        </form>
  
        <div class="links" style="justify-content: center; margin-top: 20px;">
           <router-link to="/login" class="sign-up">Back to Login</router-link>
            <router-link v-if="tokenError" to="/forgot-password" class="forgot-password" style="margin-left: 10px;">Request New Link</router-link>
         </div>
      </div>
    </div>
  </template>
  
  <script>
  // Option 1: If using Vuex (less common for reset password)
  // import { mapActions } from 'vuex';
  
  // Option 2: If using an API service directly
  import authService from '@/services/authService'; // Adjust path as needed
  
  export default {
    name: 'ResetPassword',
    data() {
      return {
        password: '',
        passwordConfirm: '',
        token: null,
        loading: false,
        error: null,
        message: null, // Used for success message
        tokenError: false,
        showPassword: false,
        showConfirmPassword: false,
      };
    },
    created() {
      // Get token from route query parameter (e.g., /reset-password?token=...)
      this.token = this.$route.query.token;
      if (!this.token) {
        this.error = 'Invalid or missing password reset token.';
        this.tokenError = true;
        console.error("Reset Password: Token missing from URL query.");
      }
    },
    methods: {
      // Option 1: If using Vuex
      // ...mapActions(['performPasswordReset']), // Assuming you create this action
  
      async handleResetPassword() {
        if (this.tokenError) return; // Don't submit if token was missing initially
  
        this.error = null;
        this.message = null;
  
        if (this.password !== this.passwordConfirm) {
          this.error = 'Passwords do not match.';
          return;
        }
         if (this.password.length < 6) {
           this.error = 'Password must be at least 6 characters long.';
           return;
        }
  
        this.loading = true;
  
        try {
           // Option 1: Call Vuex action
           // const response = await this.performPasswordReset({ token: this.token, password: this.password });
           // this.message = response?.data?.message || 'Password updated successfully. You can now login.';
  
           // Option 2: Call API Service directly
           const response = await authService.resetPassword(this.token, { password: this.password });
           this.message = response.data.message; // Display success message from backend
  
  
        } catch (err) {
          console.error("Reset Password Error:", err);
          this.error = err?.response?.data?.message || err.message || 'An error occurred while updating the password.';
          // Check if the error indicates an invalid/expired token
           if (err.response?.status === 400 || err.response?.data?.message.toLowerCase().includes('token')) {
               this.tokenError = true; // Set token error flag to maybe show different UI/links
               this.error = 'Password reset link is invalid or has expired. Please request a new one.' // Override error message
           }
        } finally {
          this.loading = false;
        }
      },
    },
  };
  </script>
  
  <style scoped>
  /* Reuse styles from Register.vue or UserLogin.vue */
  .login-container { display: flex; justify-content: center; align-items: center; height: 100vh; background: linear-gradient(-45deg, #141e30, #243b55, #141e30); background-size: 400% 400%; animation: gradientAnimation 10s ease infinite; }
  .glass-card { background: rgba(255, 255, 255, 0.15); backdrop-filter: blur(12px); border-radius: 12px; padding: 40px; text-align: center; box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2); width: 380px; /* Match register width */ max-width: 100%; }
  .title { font-size: 24px; color: #fff; font-weight: bold; margin-bottom: 5px; }
  .subtitle { color: rgba(255, 255, 255, 0.8); font-size: 14px; margin-bottom: 20px; }
  .input-wrapper { width: 100%; position: relative; margin-bottom: 15px; }
  .input-field { width: 100%; max-width: 100%; box-sizing: border-box; padding: 12px; border: none; border-radius: 8px; font-size: 16px; background: rgba(255, 255, 255, 0.2); color: #fff; outline: none; }
  .input-field::placeholder { color: rgba(255, 255, 255, 0.7); }
  .password-container { position: relative; }
  .toggle-password { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); cursor: pointer; font-size: 18px; color: rgba(255, 255, 255, 0.7); }
  .login-btn { width: 100%; max-width: 100%; padding: 12px; font-size: 16px; font-weight: 500; border: none; border-radius: 8px; cursor: pointer; transition: transform 0.2s, box-shadow 0.2s; background: #4CAF50; color: white; }
  .login-btn:disabled { background: #aaa; cursor: not-allowed; }
  .login-btn:hover:enabled { transform: scale(1.05); box-shadow: 0 4px 10px rgba(255, 255, 255, 0.3); }
  .links { display: flex; justify-content: space-between; align-items: center; margin-top: 15px; font-size: 14px; }
  .sign-up, .forgot-password { color: rgba(255, 255, 255, 0.8); text-decoration: none; transition: color 0.3s; }
  .sign-up:hover, .forgot-password:hover { color: #4CAF50; }
  .error-message { background-color: rgba(255, 0, 0, 0.2); color: #ffdddd; padding: 10px; border-radius: 5px; margin-bottom: 15px; border: 1px solid rgba(255, 0, 0, 0.3); font-size: 14px; }
  .success-message { background-color: rgba(0, 255, 0, 0.15); color: #ddffdd; padding: 10px; border-radius: 5px; margin-bottom: 15px; border: 1px solid rgba(0, 255, 0, 0.2); font-size: 14px; }
  @keyframes gradientAnimation { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
  </style>